import React, { createContext, useContext } from 'react';
import { Box, Stack, Typography, Paper } from '@mui/material';
import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts';
import { BarChartDatum } from '../assetAllocationPieChart';
import { formatPercentValue } from '@/utils/numbers';

// eslint-disable-next-line react-refresh/only-export-components
export const BarChartDataContext = createContext<BarChartDatum[]>([]);

function AxisTooltipContentSorted() {
  const dataSet = useContext(BarChartDataContext);
  const axesData = useAxesTooltip();

  if (!axesData?.length) {
    return null;
  }

  return (
    <>
      {axesData.map(({ seriesItems, dataIndex }) => {
        // Index access is typed as non-undefined, but a stale/out-of-range
        // dataIndex (or an empty context) can yield undefined at runtime.
        const row = dataSet[dataIndex] as BarChartDatum | undefined;
        if (!row) return null;

        return (
          <Paper
            key={`tooltip-${String(dataIndex)}`}
            elevation={3}
            sx={{ p: 1.5, pointerEvents: 'none' }}
          >
            <Stack>
              {seriesItems.map((item) => {
                return (
                  <Stack
                    key={item.seriesId}
                    direction='row'
                    alignItems='flex-start'
                    gap={3}
                  >
                    <Stack
                      direction='row'
                      alignItems='center'
                      gap={1}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: item.color,
                        }}
                      />
                      <Typography variant='body2'>{row.name || ''}</Typography>
                    </Stack>

                    <Stack
                      direction='column'
                      alignItems='flex-end'
                    >
                      <Typography variant='body2'>
                        {item.formattedValue}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='textSecondary'
                      >
                        ({formatPercentValue(row.originalRecord.percent)})
                      </Typography>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Paper>
        );
      })}
    </>
  );
}

/** Custom tooltip to display drill down name, value, and percentage.
 * Example: Stocks -> Domestic / $5,695,830.32 / (78.92%)
 */
export function AssetAllocationBarChartTooltip(
  props: React.ComponentProps<typeof ChartsTooltipContainer>,
) {
  return (
    <ChartsTooltipContainer
      {...props}
      trigger='axis'
    >
      <AxisTooltipContentSorted />
    </ChartsTooltipContainer>
  );
}
